/**
 * useContentAnalysis Hook
 * 
 * React Query hooks for fetching content quality metrics.
 */

import { useQuery } from '@tanstack/react-query';
import { 
  getContentAnalysisSummary, 
  getContentAnalysisPages,
  getContentAnalysisTrends,
  type GetPagesParams,
  type GetTrendsParams
} from '@/services/customer/contentAnalysisApi';
import type { 
  ContentAnalysisSummaryResponse,
  ContentAnalysisPageDetailResponse,
  ContentAnalysisTrendsResponse
} from '@/types/customer';
import { useSiteStore } from '@/store/site.store';

export interface UseContentAnalysisSummaryOptions {
  siteId?: number;
  enabled?: boolean;
}

export interface UseContentAnalysisPagesOptions extends GetPagesParams {
  siteId?: number;
  enabled?: boolean;
}

export interface UseContentAnalysisTrendsOptions extends GetTrendsParams {
  siteId?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch content analysis summary
 */
export function useContentAnalysisSummary(options: UseContentAnalysisSummaryOptions = {}) {
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const siteId = options.siteId ?? selectedSite?.id ?? 1;

  return useQuery<ContentAnalysisSummaryResponse, Error>({
    queryKey: ['content-analysis-summary', siteId],
    queryFn: () => getContentAnalysisSummary(siteId),
    enabled: options.enabled !== false,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch paginated content analysis pages
 */
export function useContentAnalysisPages(options: UseContentAnalysisPagesOptions = {}) {
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const siteId = options.siteId ?? selectedSite?.id ?? 1;
  
  const { page, size, sortBy, sortDirection, qualityFilter, enabled, ...rest } = options;

  return useQuery<ContentAnalysisPageDetailResponse, Error>({
    queryKey: ['content-analysis-pages', siteId, page, size, sortBy, sortDirection, qualityFilter],
    queryFn: () => getContentAnalysisPages(siteId, { page, size, sortBy, sortDirection, qualityFilter }),
    enabled: enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch content quality trends
 */
export function useContentAnalysisTrends(options: UseContentAnalysisTrendsOptions = {}) {
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const siteId = options.siteId ?? selectedSite?.id ?? 1;

  const { startDate, endDate, enabled, ...rest } = options;

  return useQuery<ContentAnalysisTrendsResponse, Error>({
    queryKey: ['content-analysis-trends', siteId, startDate, endDate],
    queryFn: () => getContentAnalysisTrends(siteId, { startDate, endDate }),
    enabled: enabled !== false,
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
}

export default {
  useContentAnalysisSummary,
  useContentAnalysisPages,
  useContentAnalysisTrends,
};
